import React, { useState, useEffect } from 'react';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import CreditNoteForm from '../components/CreditNoteForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useCreateCreditNoteHandler } from '../dataHandlers/createCreditNoteHandler';
import {
  CREDIT_NOTE_DOCUMENT_TYPE_CODE,
  GOODS_AND_SERVICE_CN_CODE,
  INVOICE_DOCUMENT_TYPE_CODE,
  OUT_OF_SCOPE_INVOICE_CODE,
} from '../../../constants/defaults';
import { usePermissions } from '../../../hooks/usePermissions';
import { CREATE_PURCHASE_CREDIT_NOTE, CREATE_SALE_CREDIT_NOTE } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function CreateCreditNotePage({ isPurchase = false }) {
  useDocumentTitle(isPurchase ? 'Self Billed Credit Note Form' : 'Credit Note Form');
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();

  useEffect(() => {
    if (loading) {
      return;
    }
    const createPermission = isPurchase ? CREATE_PURCHASE_CREDIT_NOTE : CREATE_SALE_CREDIT_NOTE;
    if (!hasEInvoicePermission(createPermission)) {
      showActionDeniedSnackbar();
      navigate(isPurchase ? '/purchases/documents?tab=credit-notes' : '/sales/documents?tab=credit-notes', {
        replace: true,
      });
    }
  }, [isPurchase, loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);
  const locationState = location.state || {};
  const { invoiceData = {}, source } = locationState;
  const document = invoiceData;
  const onCreditNoteCreated = () => {
    navigate(isPurchase ? '/purchases/documents?tab=credit-notes' : '/sales/documents?tab=credit-notes');
  };
  const isLinkedFlow = source === 'invoice';

  const { onSubmit, error, isPending } = useCreateCreditNoteHandler({ onSuccess: onCreditNoteCreated, isPurchase });

  // Reset isSubmitting when mutation completes
  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const onSaveAsEInv = async (payload) => {
    setIsSubmitting(true);
    onSubmit(payload);
  };

  const onCancel = async () => {
    navigate(
      location.state?.from ||
        (isPurchase ? '/purchases/documents?tab=credit-notes' : '/sales/documents?tab=credit-notes'),
    );
  };

  const { document_number, issue_date, invoice_type_code, ...rest } = { ...document };

  const docTypeCodeMap = {
    [INVOICE_DOCUMENT_TYPE_CODE]: CREDIT_NOTE_DOCUMENT_TYPE_CODE,
    [OUT_OF_SCOPE_INVOICE_CODE]: GOODS_AND_SERVICE_CN_CODE,
  };

  return (
    <Stack direction="column" sx={{ mt: '-2rem' }}>
      <CreditNoteForm
        initialData={
          {
            ...rest,
            credit_note_type_code: docTypeCodeMap[invoice_type_code] ?? null,
            billing_reference: [
              {
                id: document_number,
                issue_date: issue_date,
              },
            ],
          } || {}
        }
        onSubmit={onSaveAsEInv}
        onCancel={onCancel}
        apiErrors={error}
        isDraftButtonEnabled={location.state?.isDraftButtonEnabled}
        isPurchase={isPurchase}
        isLoading={isPending || isSubmitting}
        isLinkedFlow={isLinkedFlow}
      />
    </Stack>
  );
}
